/* ============================================================
   MA Split Hero
   - IntersectionObserver: autoplay / pause video
   - Carousel: dot navigation, arrow buttons, drag-to-swipe, auto-advance
   ============================================================ */

(function () {
  'use strict';

  class SplitHero extends HTMLElement {
    connectedCallback() {
      this.mediaEl  = this.querySelector('.ma-split-hero__media');
      this.autoplay = this.dataset.autoplay === 'true';

      this.videos = Array.from(this.querySelectorAll('.ma-split-hero__video'));
      if (this.videos.length > 0) {
        this.initVideoObserver();
      }

      this.track = this.querySelector('[data-carousel-track]');
      if (this.track) {
        this.initCarousel();
      }
    }

    disconnectedCallback() {
      if (this.videoObserver) this.videoObserver.disconnect();
      if (this._resizeOff)    this._resizeOff();
      this.stopAutoplay();
    }

    /* ── Video autoplay ─────────────────────────────────────── */

    initVideoObserver() {
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (this.autoplay) this.videos.forEach((v) => v.play().catch(() => {}));
            } else {
              this.videos.forEach((v) => { if (!v.paused) v.pause(); });
            }
          });
        },
        { threshold: 0.25 }
      );
      this.videoObserver.observe(this.mediaEl);
    }

    /* ── Carousel ───────────────────────────────────────────── */

    initCarousel() {
      this.slides   = Array.from(this.track.querySelectorAll('.ma-split-hero__carousel-slide'));
      this.dots     = Array.from(this.querySelectorAll('.ma-split-hero__carousel-dot'));
      this.prevBtn  = this.querySelector('[data-carousel-prev]');
      this.nextBtn  = this.querySelector('[data-carousel-next]');
      this.currentIndex = 0;

      /* Dot clicks */
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this.stopAutoplay();
          this.goTo(i);
          this.startAutoplay();
        });
      });

      /* Arrow clicks */
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.stopAutoplay();
          this.goTo(Math.max(this.currentIndex - 1, 0));
          this.startAutoplay();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.stopAutoplay();
          this.goTo(Math.min(this.currentIndex + 1, this.slides.length - 1));
          this.startAutoplay();
        });
      }

      /* Drag-to-swipe */
      this.initDrag();

      /* Pause auto-advance while hovering */
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());

      /* Recalculate on resize — also stop/start autoplay when crossing mobile breakpoint */
      const onResize = () => {
        this.snapTo(this.currentIndex);
        this.stopAutoplay();
        this.startAutoplay();
      };
      window.addEventListener('resize', onResize);
      this._resizeOff = () => window.removeEventListener('resize', onResize);

      this.snapTo(this.currentIndex);
      this.updateDots();
      this.updateArrows();
      this.startAutoplay();
    }

    initDrag() {
      let startX        = 0;
      let startY        = 0;
      let baseOffset    = 0;
      let dragging      = false;
      let determined    = false;  // have we decided horiz vs vert?
      let isHorizontal  = false;

      this.track.addEventListener('touchstart', (e) => {
        startX     = e.touches[0].clientX;
        startY     = e.touches[0].clientY;
        baseOffset = this.slideOffset(this.currentIndex);
        dragging   = true;
        determined = false;
        isHorizontal = false;
        this.stopAutoplay();
        /* Kill transition so the track follows the finger exactly */
        this.track.style.transition = 'none';
      }, { passive: true });

      this.track.addEventListener('touchmove', (e) => {
        if (!dragging) return;
        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;

        /* Wait until there's enough movement to determine direction */
        if (!determined && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
          determined   = true;
          isHorizontal = Math.abs(dx) > Math.abs(dy);
        }

        if (!isHorizontal) return;

        /* Prevent page scroll while dragging the carousel */
        e.preventDefault();

        /* Rubber-band resistance at first/last slide */
        const atStart = this.currentIndex === 0 && dx > 0;
        const atEnd   = this.currentIndex === this.slides.length - 1 && dx < 0;
        const drag    = (atStart || atEnd) ? dx * 0.25 : dx;

        this.track.style.transform = `translateX(${-baseOffset + drag}px)`;
      }, { passive: false });

      this.track.addEventListener('touchend', (e) => {
        if (!dragging) return;
        dragging = false;

        /* Re-enable CSS transition before snapping */
        this.track.style.transition = '';
        /* Force a reflow so the browser registers the transition restore
           before we immediately set a new transform value */
        void this.track.getBoundingClientRect();

        if (!isHorizontal) {
          this.snapTo(this.currentIndex);
          this.startAutoplay();
          return;
        }

        const dx        = e.changedTouches[0].clientX - startX;
        const threshold = this.slides[0].offsetWidth * 0.25;

        let target = this.currentIndex;
        if      (dx < -threshold && target < this.slides.length - 1) target++;
        else if (dx >  threshold && target > 0)                       target--;

        this.goTo(target);
        this.startAutoplay();
      }, { passive: true });
    }

    goTo(index) {
      this.currentIndex = index;
      this.snapTo(index);
      this.updateDots();
      this.updateArrows();
    }

    /* snapTo sets the transform without touching dots/arrows */
    snapTo(index) {
      if (!this.slides || !this.slides.length) return;
      this.track.style.transform = `translateX(${-this.slideOffset(index)}px)`;
    }

    /* Pixel offset of slide[index] from the start of the track */
    slideOffset(index) {
      return this.slides[index].offsetLeft - this.track.offsetLeft;
    }

    updateDots() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.currentIndex);
        dot.setAttribute('aria-selected', String(i === this.currentIndex));
      });
    }

    updateArrows() {
      if (this.prevBtn) this.prevBtn.disabled = this.currentIndex === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentIndex === this.slides.length - 1;
    }

    startAutoplay() {
      if (window.innerWidth <= 749) return;
      const secs = parseFloat(this.dataset.interval || '0');
      if (!secs || this.slides.length < 2) return;
      this._timer = setInterval(() => {
        this.goTo((this.currentIndex + 1) % this.slides.length);
      }, secs * 1000);
    }

    stopAutoplay() {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  if (!customElements.get('split-hero')) customElements.define('split-hero', SplitHero);
})();
